import { useQuery } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import React, { createRef, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import type { DataProvider } from "../../apiClient/generated";
import { LoadingIndicator } from "../../components/MapV2/ui/LoadingIndicator";
import { useDataProvidersApi } from "../../hooks";
import { Card } from "../../ui/Card";

const providersWithLicense = [
    {
        name: "EMIT",
        label: "EMIT",
    },
    {
        name: "UNEP IMEO",
        label: "UNEP IMEO",
    },
    {
        name: "EDF MethaneAir",
        label: "MethaneSAT + Google Earth Engine",
    },
    {
        name: "CarbonMapper",
        label: "Carbon Mapper",
    },
];

export const Licensing = () => {
    useDocumentTitle("Licensing - AERSHED");

    const apiClient = useDataProvidersApi();
    const { search } = useLocation();
    const sectionId = new URLSearchParams(search).get("section_id");

    const scrollContainer = createRef<HTMLDivElement>();

    useEffect(() => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [scrollContainer]);

    const query = useQuery({
        queryKey: ["enabledDataProviders"],
        queryFn: async () => {
            return await apiClient.dataProvidersList({
                isEnabled: true,
            });
        },
        staleTime: Infinity,
    });

    const dataProviders = useMemo(() => {
        return query.data?.results || [];
    }, [query.data?.results]);

    const listedProviders = useMemo(() => {
        // Only display providers that we have a license for
        return dataProviders
            .filter((provider) =>
                providersWithLicense.map((p) => p.name).includes(provider.name),
            )
            .map((provider) => ({
                ...provider,
                label: providersWithLicense.find(
                    (p) => p.name === provider.name,
                )?.label,
            }));
    }, [dataProviders]);

    if (query.isLoading) {
        return <LoadingIndicator />;
    }

    const licenses = listedProviders.map((provider) => {
        const ref =
            sectionId && provider.name === sectionId ? scrollContainer : null;
        if (provider.label === "EMIT") {
            return (
                <EmitLicense key={provider.id} provider={provider} ref={ref} />
            );
        } else if (provider.label === "UNEP IMEO") {
            return (
                <UnepImeoLicense
                    key={provider.id}
                    provider={provider}
                    ref={ref}
                />
            );
        } else if (provider.label === "MethaneSAT + Google Earth Engine") {
            return (
                <EdfMethaneAirLicense
                    key={provider.id}
                    provider={provider}
                    ref={ref}
                />
            );
        } else if (provider.label === "Carbon Mapper") {
            return (
                <CarbonMapperLicense
                    key={provider.id}
                    provider={provider}
                    ref={ref}
                />
            );
        }
    });

    return (
        <div className="prose px-6 max-w-6xl mx-auto">
            <h1 className="flex pt-10 font-bold font-sans antialiased text-inherit text-xl">
                Licensing
            </h1>

            <Card className="px-10">
                <div className="text-base">
                    <p className="mt-2">
                        Portions of the information contained in AERSHED derive
                        from underlying third-party sources.
                    </p>
                    <p className="mt-2">
                        Aerscape is required to include the following text as
                        part of making the underlying data available to you. The
                        language and the applicable licensing requirements
                        differ among the third-party sources, which include the
                        following:
                    </p>
                    <ul className="list-inside list-disc ml-2 mt-2">
                        {listedProviders.map((provider) => (
                            <li key={provider.id}>
                                <a href={`#${provider.name}`}>
                                    {provider.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
                {licenses}
            </Card>
        </div>
    );
};

interface DataProviderWithLabel extends DataProvider {
    label: string;
}

type LicenseProps = {
    provider: DataProviderWithLabel;
};

const EmitLicense = React.forwardRef<HTMLDivElement, LicenseProps>(
    ({ provider }, ref) => (
        <section className="pt-3" id={provider.name} ref={ref}>
            <h3 className="underline flex font-bold font-sans antialiased text-inherit text-xl">
                {provider.label}
            </h3>
            <div className="text-base mt-4">
                <p>
                    This source is based on the Multi-Mission Geographic
                    Information System (“MMGIS”), a free and open-source
                    geospatial application created by NASA AMMOS, an Open Source
                    License approved by CalTech/JPL.
                </p>
                <p className="mt-2 ml-8">
                    <a
                        href="https://nasa-ammos.github.io/MMGIS/license"
                        target="_blank"
                        rel="noreferrer"
                    >
                        https://nasa-ammos.github.io/MMGIS/license
                    </a>
                </p>
                <p className="mt-2">
                    Copyright (c) 2022 California Institute of Technology
                    (“Caltech”). U.S. Government sponsorship acknowledged.
                </p>
                <p className="mt-2">
                    Redistribution and use in source and binary forms, with or
                    without modification, are permitted provided that the
                    following conditions are met:
                </p>
                <ul className="list-inside list-disc ml-2 mt-2">
                    <li>
                        Redistributions of source code must retain the above
                        copyright notice, this list of conditions and the
                        following disclaimer.
                    </li>
                    <li>
                        Redistributions in binary form must reproduce the above
                        copyright notice, this list of conditions and the
                        following disclaimer in the documentation and/or other
                        materials provided with the distribution.
                    </li>
                    <li>
                        Neither the name of Caltech nor its operating division,
                        the Jet Propulsion Laboratory, nor the names of its
                        contributors may be used to endorse or promote products
                        derived from this software without specific prior
                        written permission.
                    </li>
                </ul>
                <p className="mt-2">
                    THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND
                    CONTRIBUTORS “AS IS” AND ANY EXPRESS OR IMPLIED WARRANTIES,
                    INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
                    MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
                    DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR
                    CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
                    SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT
                    NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
                    LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
                    HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
                    CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR
                    OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
                    SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
                </p>
                <p className="mt-2">
                    Licensed under the Apache License, Version 2.0 (the
                    "License"); you may not use this file except in compliance
                    with the License. You may obtain a copy of the License at
                </p>
                <p className="mt-2 ml-8">
                    <a
                        href="http://www.apache.org/licenses/LICENSE-2.0"
                        target="_blank"
                        rel="noreferrer"
                    >
                        http://www.apache.org/licenses/LICENSE-2.0
                    </a>
                </p>
                <p className="mt-2">
                    Unless required by applicable law or agreed to in writing,
                    software distributed under the License is distributed on an
                    "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND,
                    either express or implied. See the License for the specific
                    language governing permissions and limitations under the
                    License.
                </p>
            </div>
        </section>
    ),
);

const UnepImeoLicense = React.forwardRef<HTMLDivElement, LicenseProps>(
    ({ provider }, ref) => (
        <section className="pt-3" id={provider.name} ref={ref}>
            <h3 className="underline flex font-bold font-sans antialiased text-inherit text-xl">
                {provider.label}
            </h3>
            <div className="text-base mt-4">
                <p>
                    UNEP's IMEO pulls data from a dozen methane-detecting Earth
                    Observation satellites to detect emissions, trace them to
                    their source, and quantify them across the globe. Data from
                    these satellites is publicly available, though sometimes
                    requires specialized remote sensing expertise to properly
                    interpret. UNEP's IMEO provides this expertise to the global
                    community to supply actionable data and further the goals of
                    the Global Methane Pledge, including through data-to-action
                    initiatives like the Methane Alert and Response System
                    (MARS).
                </p>
                <p className="mt-2">
                    United Nations Disclaimer: The designations employed and the
                    presentation of material do not imply the expression of any
                    opinion whatsoever on the part of the Secretariat of the
                    United Nations concerning the legal status of any country,
                    territory, city or area or of its authorities, or concerning
                    the delimitation of its frontiers or boundaries.
                </p>
                <p className="mt-2">
                    This data is made available under a{" "}
                    <a
                        href="https://creativecommons.org/licenses/by-nc-sa/4.0/legalcode.en"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Creative Commons license CC BY-NC-SA 4.0 DEED
                        (Attribution-NonCommercial-ShareAlike 4.0 International)
                    </a>
                    .
                </p>
                <p className="mt-2">
                    Aerscape has included this data at no additional cost as an
                    additional benefit to AERSHED users that is separate from
                    the data management and analytics services provided to
                    clients at cost.
                </p>
                <p className="mt-2 ml-8">
                    <a
                        href="https://methanedata.azurewebsites.net/export"
                        target="_blank"
                        rel="noreferrer"
                    >
                        https://methanedata.azurewebsites.net/export
                    </a>
                </p>
            </div>
        </section>
    ),
);

const EdfMethaneAirLicense = React.forwardRef<HTMLDivElement, LicenseProps>(
    ({ provider }, ref) => (
        <section className="pt-3" id={provider.name} ref={ref}>
            <h3 className="underline flex font-bold font-sans antialiased text-inherit text-xl">
                {provider.label}
            </h3>
            <div className="text-base mt-4">
                <p>
                    This data is from MethaneSAT and is provided pursuant to the
                    MethaneSAT+Google Earth Engine Terms of Use, to which you
                    are bound. These Terms of Use are available here:{" "}
                </p>
                <p className="mt-2 ml-8">
                    <a
                        href="https://www.edf.org/sites/default/files/2024-02/MethaneSAT%20-%20Content%20License%20Terms%20of%20Use%202024_0.pdf"
                        target="_blank"
                        rel="noreferrer"
                    >
                        MethaneSAT - Content License Terms of Use 2024_0.pdf
                    </a>
                </p>
                <p className="mt-2">
                    Download the most current dataset at Google Earth Engine.
                </p>
                <p className="mt-2">
                    If you share or use the data in any manner with any third
                    parties, you must:
                </p>
                <ul className="list-inside list-disc ml-2 mt-2">
                    <li>
                        (a) explicitly state to these third parties that they
                        are agreeing to be bound by the Terms of Use;
                    </li>
                    <li>
                        (b) display a citation that states: “Data from
                        MethaneSAT” and “Download the most current dataset at
                        Google Earth Engine”; and
                    </li>
                    <li>
                        (c) explicitly state to these third parties that, if
                        such third party creates a further project containing
                        the Data, any such users of that project must also agree
                        to be bound by these Terms of Use.
                    </li>
                </ul>
                <p className="mt-2">
                    These data were provided as an early release product to
                    facilitate timely delivery to operators. False positives may
                    exist in the data. EDF/Harvard is interested in feedback. By
                    using these data you agree to consider these an interim
                    product that may be subject to errors and revision.
                </p>
            </div>
        </section>
    ),
);

const CarbonMapperLicense = React.forwardRef<HTMLDivElement, LicenseProps>(
    ({ provider }, ref) => (
        <section className="pt-3" id={provider.name} ref={ref}>
            <h3 className="underline flex font-bold font-sans antialiased text-inherit text-xl">
                {provider.label}
            </h3>
            <div className="text-base mt-4">
                <p>
                    Carbon Mapper's data is derived from NASA's next generation
                    Airborne Visible/Infrared Imaging Spectrometer (AVIRIS-NG),
                    NASA's EMIT instrument, and ASU's Global Airborne
                    Observatory (GAO). You have access to this data as a
                    “Permitted Use” pursuant to your Carbon Mapper Data License
                    Agreement, which allows for “Commercial Use; Limited
                    Distribution and Publication,” which is defined as follows:
                </p>
                <p className="mt-2 ml-4">
                    Licensee's use is not bound by the Public License.
                </p>
                <p className="mt-2">
                    Licensee (including its Affiliates and Representatives) is
                    permitted to access and use the Data: (a) for Licensee’s own
                    internal business purposes; (b) to store, display, and
                    distribute Data with no further rights of distribution by
                    Licensee or any End User permitted unless otherwise
                    expressly agreed to via an amendment to this Data License
                    Agreement; (c) to create and distribute Derivative Products
                    to third parties; and (d) publish articles related to or
                    based upon the Data. Licensee agrees that all such
                    distribution and publications must include an attribution
                    that clearly and conspicuously identifies “Carbon Mapper
                    Inc.” as the source of the Data.
                </p>
                <p className="mt-2">
                    Aerscape is a “Representative” of yours as defined in the
                    Carbon Mapper Data License Agreement.
                </p>
            </div>
        </section>
    ),
);
